import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { BRAND } from "@/styles/brand";
import confetti from "canvas-confetti";

const C = BRAND.colors;

export default function ScratchCard({
  reward = { emoji: "🍵", title: "Gratis Matcha Latte", description: "Dein Willkommensgeschenk!" },
  width = 300,
  height = 200,
  revealThreshold = 50,
  onReveal,
}) {
  const canvasRef = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [scratchPercent, setScratchPercent] = useState(0);
  const isDrawing = useRef(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Gold scratch surface
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#D4A574");
    gradient.addColorStop(0.3, "#E8C49A");
    gradient.addColorStop(0.6, "#D4A574");
    gradient.addColorStop(1, "#C49A6A");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Subtle pattern
    ctx.globalAlpha = 0.15;
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      ctx.beginPath();
      ctx.arc(x, y, 4 + Math.random() * 6, 0, Math.PI * 2);
      ctx.fillStyle = "#8B6914";
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // "Hier kratzen" text
    ctx.font = `bold 18px ${BRAND.typography.heading}`;
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.textAlign = "center";
    ctx.fillText("Hier kratzen!", width / 2, height / 2 - 5);

    ctx.font = `14px ${BRAND.typography.body}`;
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText("👆 Mit dem Finger wischen", width / 2, height / 2 + 20);
  }, [width, height]);

  useEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed) return;
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 24, 0, Math.PI * 2);
    ctx.fill();

    // Calculate scratch percentage
    const imageData = ctx.getImageData(0, 0, width, height);
    let cleared = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) cleared++;
    }
    const percent = (cleared / (width * height)) * 100;
    setScratchPercent(percent);

    if (percent >= revealThreshold && !isRevealed) {
      setIsRevealed(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: [C.green, C.orange, C.tan, "#FFD700"],
      });
      if (onReveal) onReveal(reward);
    }
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  };

  const handleStart = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const { x, y } = getPos(e);
    scratch(x, y);
  };

  const handleMove = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const { x, y } = getPos(e);
    scratch(x, y);
  };

  const handleEnd = () => {
    isDrawing.current = false;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative overflow-hidden mx-auto"
      style={{
        width,
        height,
        borderRadius: 24,
        boxShadow: BRAND.shadows.md,
      }}
    >
      {/* Reward underneath */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
        style={{ backgroundColor: C.bgCard }}>
        <motion.div
          animate={isRevealed ? { scale: [1, 1.2, 1] } : {}}
          transition={{ duration: 0.5 }}
        >
          <span className="text-5xl block mb-3">{reward.emoji}</span>
        </motion.div>
        <h3 className="text-lg font-bold"
          style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
          {reward.title}
        </h3>
        <p className="text-sm mt-1" style={{ color: C.textMuted }}>
          {reward.description}
        </p>
        {isRevealed && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileTap={{ scale: 0.95 }}
            className="mt-4 px-6 py-2.5 rounded-xl text-sm font-bold text-white"
            style={{ backgroundColor: C.green }}>
            Coupon aktivieren
          </motion.button>
        )}
      </div>

      {/* Scratch Canvas */}
      {!isRevealed && (
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="absolute inset-0 cursor-pointer"
          style={{ touchAction: "none" }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      )}
    </motion.div>
  );
}
